import React, { useEffect } from "react";
import { useStore } from "../../store/store";
import findTitle24 from "../../util/findTitle24";

interface DisplayTitle24Args {
  ufactor: string;
  shgc: string;
  cpd?: string;
}

function useTitle24Selector() {
  const addToQuote = useStore((state) => state.addToQuote);

  const ufactor = useStore((state) => state.quote.ufactor);
  const shgc = useStore((state) => state.quote.shgc);

  const exactConfig = useStore((state) => state.quote.exactConfig);
  const stilesAndRails = useStore((state) => state.quote.stilesAndRails);
  const lockRail = useStore((state) => state.quote.lockRail);
  const material = useStore((state) => state.quote.material);
  const glassType = useStore((state) => state.quote.glassType);
  const sillStyle = useStore((state) => state.quote.sillStyle);
  const doorType = useStore((state) => state.quote.doorType);
  const i89coating = useStore((state) => state.quote.i89coating);
  const addDL = useStore((state) => state.quote.addDL);
  const liteConfig = useStore((state) => state.quote.liteConfig);
  const breatherTubes = useStore((state) => state.quote.breatherTubes);
  const swingDirection = useStore((state) => state.quote.swingDirection);
  const woodSpecies = useStore((state) => state.quote.woodSpecies);
  const addScreen = useStore((state) => state.quote.addScreen);
  const stileCategory = useStore((state) => state.quote.stileCategory);
  const customGlass = useStore((state) => state.quote.customGlass);
  const alumThickFrame = useStore((state) => state.quote.alumThickFrame);
  const glassAddon = useStore((state) => state.quote.glassAddon);

  const text =
    "NOTE: Select Stiles, Material, Sill, and Glass to view U-Factor and SHGC numbers here";

  const intUfactor = parseFloat(ufactor || "");
  const intShgc = parseFloat(shgc || "");

  // const title24Compliant = intUfactor <= 0.3 && intShgc <= 0.23;

  const isAluminum = material === "All Aluminum";

  useEffect(() => {
    async function handletitle24() {
      if (customGlass) {
        return displayTitle24();
      }

      if (isAluminum) {
        const title24 = await findTitle24({
          glassAddon: glassAddon || null,
          doorType,
          material,
          glass: glassType || "",
          i89: i89coating || "",
          liteConfig: liteConfig || "",
          breatherTubes: breatherTubes || "None",
        });

        if (title24 !== "Error") displayTitle24(title24);
      } else {
        let stiles =
          stilesAndRails?.includes("Slimline") &&
          lockRail?.includes('4-3/4" Lock Stile')
            ? '2-1/2" Stiles and Rails with 3-7/16" Lock Stile'
            : stilesAndRails?.includes("Slimline")
              ? lockRail
              : stilesAndRails;

        const nonSliderFetchT24 =
          (material &&
            glassType &&
            stiles &&
            sillStyle &&
            sillStyle !== "Raised Sill" &&
            doorType !== "Slider") ||
          (doorType === "Fixed Panel" && material && glassType && stiles) ||
          (doorType === "Pivot" && material && glassType && stiles) ||
          (material === "All Aluminum" && glassType)
            ? true
            : false;

        const sliderFetchT24 =
          material && glassType && stiles && doorType === "Slider"
            ? true
            : false;

        if (!nonSliderFetchT24 && !sliderFetchT24) return;

        if (nonSliderFetchT24) {
          const title24 = await findTitle24({
            glassAddon: glassAddon || null,
            doorType: doorType || "",
            material,
            glass: glassType || "",
            i89: i89coating || "",
            stiles:
              stilesAndRails?.includes("Slimline") && material === "All Wood"
                ? "Slimline"
                : stiles
                  ? stiles
                  : "",
            stileCategory: stileCategory || "",
            liteConfig: liteConfig || "",
            breatherTubes: breatherTubes || "None",
            wood: woodSpecies,
            addScreen: addScreen || "",
            swingDirection: swingDirection || "",
            sill: sillStyle || "",
            addDL,
            exactConfig,
            alumThickFrame: alumThickFrame || false,
          });

          if (title24 !== "Error") displayTitle24(title24);
        } else if (sliderFetchT24) {
          const title24 = await findTitle24({
            glassAddon: glassAddon || null,
            doorType,
            material,
            glass: glassType || "",
            i89: i89coating || "",
            stiles: stiles || "",
            stileCategory: stileCategory || "",
            liteConfig: liteConfig || "",
            breatherTubes: breatherTubes || "None",
            wood: woodSpecies,
            addScreen: addScreen || "",
            swingDirection: "",
            sill: sillStyle || "",
          });

          if (title24 !== "Error") displayTitle24(title24);
        }
      }
    }

    handletitle24();
  }, [
    material,
    woodSpecies,
    glassType,
    stilesAndRails,
    sillStyle,
    doorType,
    breatherTubes,
    addScreen,
    liteConfig,
    lockRail,
    i89coating,
    exactConfig,
    glassAddon,
  ]);

  const displayTitle24 = (title24?: DisplayTitle24Args) => {
    if (!title24) {
      return addToQuote({
        ufactor: "0.53",
        shgc: "0.53",
        cpd: "",
      });
    }

    let update: DisplayTitle24Args = {
      ufactor: title24.ufactor,
      shgc: title24.shgc,
    };
    if (title24.cpd) {
      update["cpd"] = title24.cpd;
    }

    addToQuote(update);
  };

  return {
    // title24Compliant,
    text,
    ufactor,
    shgc,
    customGlass,
    intUfactor,
    intShgc,
    isAluminum,
  };
}

export default useTitle24Selector;
